import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetGroupOccupancyQuery } from '../../../api/group/occupancyApiSlice';
import AssetDetailSection from './AssetDetailSection';
import { OCCUPANCY_HATCH as HATCH } from './occupancyHatch';
import { utilizationErrorKind } from './utilizationError';
import { peaksByHour, reservedByHour, visibleHours } from './utilizationHours';
import { utilizationRange } from './utilizationRange';
import { zoneOfLocation, zonedFormatter } from '../../../utils/stationTime';

// Utilization for the asset's group: how much capacity is taken, slot by slot.
//
// The first surface in the product on which these numbers can be inspected at
// all. It reads the new-backend occupancy endpoint, which is derived from the
// booking guard rather than from either availability endpoint — so a
// disagreement between this view and a booking screen is a defect in that
// screen, visible instead of hidden. See
// docs/reviews/availability-computation-divergence.md.
//
// Capacity is per *group*, not per asset: assets in a group are fungible and
// are assigned at the moment usage starts, so "3 of 10 taken at 14:00" is the
// only meaningful statement. A unique object (venue, apartment) is the same
// calculation with capacity 1.

interface UtilizationSectionProps {
    groupId: string;
}

const RANGE_DAYS = [
    { days: 7, key: 'seven_days' },
    { days: 14, key: 'fourteen_days' },
    { days: 31, key: 'thirty_one_days' },
] as const;

// Which hours are drawn is decided by the data, in `utilizationHours` — the
// working day is the floor, not the ceiling.

// Utilization is the one place a non-primary color earns its keep: the cell
// scale has to read at a glance, and a single-hue ramp of the tenant primary is
// exactly that — opacity steps, no new palette.
const cellClass = (used: number, capacity: number): string => {
    if (capacity <= 0) return 'bg-foreground/5';
    if (used <= 0) return 'bg-foreground/5';
    const ratio = used / capacity;
    if (ratio >= 1) return 'bg-primary';
    if (ratio >= 0.75) return 'bg-primary/75';
    if (ratio >= 0.5) return 'bg-primary/50';
    if (ratio >= 0.25) return 'bg-primary/30';
    return 'bg-primary/15';
};

// The count sits on the cell, not only in a hover tooltip: colour alone is not
// an accessible encoding (WCAG 1.4.1), and a tooltip on a `div` is unreachable
// by keyboard and on touch. On the two darkest steps the fill is too strong for
// foreground text, so the count flips to the button-contrast token.
const cellTextClass = (used: number, capacity: number): string =>
    capacity > 0 && used / capacity >= 0.75
        ? 'text-text-buttonContrast'
        : 'text-foreground/70';

// The hatch for unfirm blocks lives in `occupancyHatch.ts`, shared with the
// operator week calendar.

const UtilizationSection: React.FC<UtilizationSectionProps> = ({ groupId }) => {
    const { t, i18n } = useTranslation();
    const [rangeDays, setRangeDays] = useState<number>(7);

    // #3219: the grid is the clock of the group's location, which only the
    // response names (`timeZone`). The first request is anchored in the
    // station default; once a response says otherwise, the range re-anchors
    // in that zone and is asked again. `data` keeps the last response while
    // that runs, so the zone does not flap back in between.
    const [gridZoneSource, setGridZoneSource] = useState<string | undefined>(undefined);
    const gridZone = zoneOfLocation(gridZoneSource);

    // Range rules — including the DST cap the backend's 31-day limit needs —
    // live in `utilizationRange`, where they are tested.
    const { from, to } = useMemo(() => utilizationRange(rangeDays, new Date(), gridZone), [rangeDays, gridZone]);

    const { data, isFetching, isError, error } = useGetGroupOccupancyQuery(
        { groupId, from, to },
        { skip: !groupId },
    );
    if (data?.timeZone && data.timeZone !== gridZoneSource) {
        setGridZoneSource(data.timeZone);
    }

    const failure = useMemo(() => utilizationErrorKind(error), [error]);

    const dateLocale = i18n.language.startsWith('de') ? 'de-DE' : 'en-GB';
    const formatDay = (isoDate: string): string => {
        const [year, month, day] = isoDate.split('-').map(Number);
        if (!year || !month || !day) return isoDate;
        return new Date(year, month - 1, day).toLocaleDateString(dateLocale, {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
        });
    };

    // Hours are read in the display timezone, not the browser's. The backend
    // groups cells into days in the location's zone (the response's
    // `timeZone`), so deriving the hour from any other clock would put a cell
    // in one day's row under another day's column.
    const cellZone = zoneOfLocation(data?.timeZone);
    const hourFormatter = useMemo(
        () => zonedFormatter('en-GB', { hour: '2-digit', hour12: false }, cellZone),
        [cellZone],
    );

    // Each day's cells bucketed into its 24 hours once, and the columns derived
    // from what those buckets hold — so an hour that is booked is always drawn.
    const rows = useMemo(() => {
        const hourOf = (iso: string): number => Number(hourFormatter.format(new Date(iso)));
        return (data?.days ?? []).map((day) => ({
            date: day.date,
            peakUsed: day.peakUsed,
            peaks: peaksByHour(day.cells, hourOf),
            reserved: reservedByHour(day.cells, hourOf),
        }));
    }, [data, hourFormatter]);

    const hours = useMemo(() => visibleHours(rows.map((row) => row.peaks)), [rows]);

    const capacity = data?.capacity ?? 0;

    // The hatch is a visual encoding, so it needs a text equivalent or it is
    // invisible to a screen reader and to anyone who cannot resolve the
    // pattern (WCAG 1.4.1) — the same reason the count sits on the cell.
    const cellLabel = (hour: number, used: number, reserved: boolean): string =>
        reserved
            ? t('asset_detail.utilization.cell_title_reserved', { hour, used, capacity })
            : t('asset_detail.utilization.cell_title', { hour, used, capacity });

    return (
        <AssetDetailSection title={t('asset_detail.sections.utilization')}>
            <div className={'flex items-center justify-between gap-4 mb-4 flex-wrap'}>
                <p className={'text-sm text-foreground/60'}>
                    {t('asset_detail.utilization.capacity', { count: capacity })}
                </p>
                <div className={'flex items-center gap-1 rounded-full bg-foreground/5 p-1'}>
                    {RANGE_DAYS.map((option) => (
                        <button
                            key={option.days}
                            type={'button'}
                            onClick={() => setRangeDays(option.days)}
                            className={`rounded-full px-3 py-1 text-xs transition-colors ${
                                rangeDays === option.days
                                    ? 'bg-primary text-text-buttonContrast'
                                    : 'text-foreground/60 hover:text-foreground'
                            }`}
                        >
                            {t(`asset_detail.utilization.range.${option.key}`)}
                        </button>
                    ))}
                </div>
            </div>

            {isError && (
                <div className={'text-sm text-foreground/60'}>
                    <p>{t('asset_detail.utilization.error')}</p>
                    {/* Which failure it was, so it can be reported without
                        devtools — see utilizationError.ts. */}
                    <p className={'text-foreground/40'}>
                        {t(`asset_detail.utilization.error_detail.${failure.kind}`, {
                            status: failure.status,
                        })}
                    </p>
                </div>
            )}

            {!isError && isFetching && (
                <p className={'text-sm text-foreground/40 italic'}>{t('common.loading')}</p>
            )}

            {!isError && !isFetching && capacity === 0 && (
                <p className={'text-sm text-foreground/60'}>
                    {t('asset_detail.utilization.no_capacity')}
                </p>
            )}

            {!isError && !isFetching && capacity > 0 && (
                <>
                    <div className={'overflow-x-auto'}>
                        <table
                            className={'w-full text-sm'}
                            aria-label={t('asset_detail.sections.utilization')}
                        >
                            <thead>
                                <tr className={'text-foreground/60 font-medium'}>
                                    <th
                                        scope={'col'}
                                        className={'text-left font-medium pb-2 pr-3 whitespace-nowrap'}
                                    >
                                        {t('asset_detail.utilization.day')}
                                    </th>
                                    {hours.map((hour) => (
                                        <th
                                            key={hour}
                                            scope={'col'}
                                            className={'font-medium pb-2 text-[10px] w-6'}
                                        >
                                            {hour}
                                        </th>
                                    ))}
                                    <th
                                        scope={'col'}
                                        className={'text-right font-medium pb-2 pl-3 whitespace-nowrap'}
                                    >
                                        {t('asset_detail.utilization.peak')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.date} className={'border-t border-foreground/5'}>
                                        <th
                                            scope={'row'}
                                            className={'py-1 pr-3 whitespace-nowrap text-left font-normal text-foreground/80'}
                                        >
                                            {formatDay(row.date)}
                                        </th>
                                        {hours.map((hour) => {
                                            const used = row.peaks[hour] ?? 0;
                                            // Only a cell that actually shows
                                            // occupancy can be unfirm; hatching
                                            // an empty cell would invent a
                                            // block that isn't there.
                                            const reserved = used > 0 && (row.reserved[hour] ?? false);
                                            return (
                                                <td
                                                    key={hour}
                                                    className={'py-1 px-[1px]'}
                                                    // `title` for the pointer, `aria-label` for everyone
                                                    // else: a cell whose only content is a colour has no
                                                    // accessible name, and an empty one would not say
                                                    // whether it means free or no data.
                                                    title={cellLabel(hour, used, reserved)}
                                                    aria-label={cellLabel(hour, used, reserved)}
                                                >
                                                    <div
                                                        className={[
                                                            'h-5 rounded-[2px] flex items-center',
                                                            'justify-center text-[9px] leading-none',
                                                            'tabular-nums',
                                                            cellClass(used, capacity),
                                                            cellTextClass(used, capacity),
                                                        ].join(' ')}
                                                        style={reserved ? HATCH : undefined}
                                                    >
                                                        {used > 0 ? used : ''}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                        <td className={'py-1 pl-3 text-right whitespace-nowrap text-foreground/80'}>
                                            {row.peakUsed} / {capacity}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={'flex items-center gap-3 mt-4 text-xs text-foreground/60'}>
                        <span>{t('asset_detail.utilization.legend.free')}</span>
                        <div className={'flex items-center gap-[2px]'}>
                            {['bg-foreground/5', 'bg-primary/15', 'bg-primary/30', 'bg-primary/50', 'bg-primary/75', 'bg-primary'].map(
                                (cls) => (
                                    <div key={cls} className={`h-3 w-5 rounded-[2px] ${cls}`} />
                                ),
                            )}
                        </div>
                        <span>{t('asset_detail.utilization.legend.full')}</span>
                        {/* The hatch means nothing without being named. Placed
                            after the ramp because it is a different axis —
                            how firm, not how full. */}
                        <span className={'flex items-center gap-[6px]'}>
                            <span
                                className={'h-3 w-5 rounded-[2px] bg-primary'}
                                style={HATCH}
                            />
                            {t('asset_detail.utilization.legend.reserved')}
                        </span>
                    </div>
                </>
            )}
        </AssetDetailSection>
    );
};

export default UtilizationSection;
